/**
 * The "an account was just created" handoff from the backend to the browser.
 *
 * Signup is a SERVER-side fact (`AuthFlowService.finalizeOAuth` decides it) and
 * the browser arrives at the app via a 302 that carries no state. Rather than
 * bolt a query param onto every possible landing destination (`returnTo` can be
 * any path, and the param would survive in shared URLs), the backend sets a
 * short-lived, JS-readable cookie on the OAuth/password exit and the first
 * client render consumes it.
 *
 * Value format: `<userId>|<method>`. No secret — the session cookie is the
 * credential; this only says "this navigation is a fresh signup".
 *
 * Backend writer: `packages/backend/src/core/auth/flow/signup-signal.cookie.ts`.
 */

export const SIGNUP_SIGNAL_COOKIE = 'naumu-signup';

/** sessionStorage guard so a reload/StrictMode double-effect can't double-fire. */
const REPORTED_KEY_PREFIX = 'naumu:signup-reported:';

export interface SignupSignal {
	userId: string;
	/** `google` | `microsoft` | `password`. */
	method: string;
}

const MAX_FIELD_LENGTH = 64;

/** Parse the raw cookie value. Returns null for anything malformed. */
export const parseSignupSignal = (raw: string | null | undefined): SignupSignal | null => {
	if (!raw) return null;
	const [userId, method] = raw.split('|');
	if (!userId || !method) return null;
	if (userId.length > MAX_FIELD_LENGTH || method.length > MAX_FIELD_LENGTH) return null;
	return { userId, method };
};

const readCookieValue = (name: string): string | null => {
	if (typeof document === 'undefined') return null;
	const prefix = `${name}=`;
	for (const part of document.cookie.split('; ')) {
		if (part.startsWith(prefix)) {
			try {
				return decodeURIComponent(part.slice(prefix.length));
			} catch {
				return null;
			}
		}
	}
	return null;
};

export const readSignupSignal = (): SignupSignal | null =>
	parseSignupSignal(readCookieValue(SIGNUP_SIGNAL_COOKIE));

/**
 * Expire the cookie. Cleared on BOTH the host-only and apex form because the
 * backend sets it host-only and a stale apex copy would otherwise shadow it.
 */
export const clearSignupSignal = (): void => {
	if (typeof document === 'undefined' || typeof window === 'undefined') return;
	const expiry = `${SIGNUP_SIGNAL_COOKIE}=; path=/; max-age=0`;
	document.cookie = expiry;
	const host = window.location.hostname;
	if (host.endsWith('naumu.ai')) document.cookie = `${expiry}; domain=.naumu.ai`;
};

/**
 * `true` the first time this is called for a given user in this tab; `false`
 * afterwards. Marks as reported as a side effect, so callers can use it as the
 * one-shot gate directly.
 */
export const claimSignupReport = (userId: string): boolean => {
	if (typeof window === 'undefined') return false;
	const key = `${REPORTED_KEY_PREFIX}${userId}`;
	try {
		if (window.sessionStorage.getItem(key)) return false;
		window.sessionStorage.setItem(key, '1');
		return true;
	} catch {
		// sessionStorage unavailable — reporting once per navigation is better
		// than never. The cookie is cleared regardless, so a reload won't repeat.
		return true;
	}
};
